import React from "react";
import { useAPIs } from "@/api";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { SKIP_ONBOARDING_STATUS_CHECK } from "@/constants";
import { OnboardingStatus, OnboardingStatusPerBusiness, onboardingStatusContext } from "./context";

const installationStatusPollMs = 10_000;

export function OnboardingStatusProvider(props: React.PropsWithChildren) {
  const { authenticatedAPI } = useAPIs();
  const businesses = useGetBusinesses();
  const [value, setValue] = React.useState<OnboardingStatusPerBusiness>(null);

  React.useEffect(() => {
    if (!businesses.data || !authenticatedAPI) return;
    if (SKIP_ONBOARDING_STATUS_CHECK) {
      setValue(
        Object.fromEntries(businesses.data.map((business) => [business.id, "completed"])) as Record<
          string,
          OnboardingStatus
        >,
      );
      return;
    }

    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      // Checking a pending installation also subscribes this user to its completion email.
      const results = await Promise.all(
        businesses.data!.map(async (business) => ({
          id: business.id,
          status: await authenticatedAPI
            .getBusinessInstallationStatus({ business_id: business.id })
            .then((response) => response.status)
            .catch(() => null),
        })),
      );
      if (stopped) return;
      setValue(
        (previous) =>
          Object.fromEntries(
            results.map(({ id, status }) => [
              id,
              status === "ready"
                ? "completed"
                : status === "failed"
                  ? "failed"
                  : status === null
                    ? previous?.[id] === "completed"
                      ? "completed"
                      : "check_error"
                    : "in_progress",
            ]),
          ) as Record<string, OnboardingStatus>,
      );
      if (results.every(({ status }) => status === "ready" || status === "failed")) return;
      if (!stopped)
        timer = setTimeout(() => {
          void check();
        }, installationStatusPollMs);
    };
    void check();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [authenticatedAPI, businesses.data]);

  return (
    <onboardingStatusContext.Provider value={value}>
      {props.children}
    </onboardingStatusContext.Provider>
  );
}
